import type {ReactElement} from 'react';
import React from 'react';
import {updatePublishDropDownState} from '@Components/poster-editor/poster-editor-reducer';
import {DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Dropdown} from '@Components/dropdown-v2';
import {openUploadingImagesModal} from '@Modals/uploading-images-modal';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {PosterPublishingOptionsPanel} from '@Panels/poster-publishing-options-panel';
import {DisplayType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {isUserPremium} from '@Libraries/user.library';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {PublishingOptionsDropdownProps} from './publishing-options-dropdown.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './publishing-options-dropdown.module.scss';

export function PublishingOptionsDropdown({
  publishMode,
  hasUnsavedChanges,
  isInitialized,
  isRepublishButton,
  areImagesUploading,
  recommendedOptions = [],
}: PublishingOptionsDropdownProps): ReactElement<any> {
  const dispatch = useAppDispatch();

  const defaultPanelFlowState = useAppSelector((state) => {
    return state.publishingOptionsPanel.selectionType;
  });

  const isPublishDropDownOpen = useAppSelector((state): boolean => {
    return state.posterEditor.isPublishDropDownOpen;
  });

  const republishRequestForRegen = (): void => {
    if (window.posterEditor?.config?.idOrder) {
      void window.posterEditor.whiteboard?.savePoster.save().then(() => {
        window.location.href = window.PMW.util.site_url(`cart/requestDownloadRegen/${window.posterEditor?.config?.idOrder}/1`);
      });
    }
  };
  const getDropDownSelector = (): ReactElement<any> => {
    return (
      <div className="flexbox">
        <Button
          id="nav-publish-option"
          data-testid={'NAV-PUBLISH-OPTION'}
          icon={isRepublishButton ? 'icon-repeat' : 'icon-publish-arrow'}
          customClasses={` ${isPublishDropDownOpen ? 'bg-neutral-6' : ''} ${isRepublishButton ? 'spacing-m-r-2' : 'publish-icon-hover-animation'} `}
          iconPlacement={IconPlacement.RIGHT}
          text={isRepublishButton ? window.i18next.t('pmwjs_republish') : window.i18next.t('pmwjs_publish')}
          type={Type.GHOST}
          size={Size.SMALL}
          disabled={isRepublishButton && !hasUnsavedChanges && !publishMode}
          isLoading={!isInitialized}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            if (isRepublishButton) {
              e.stopPropagation();
              republishRequestForRegen();
            }
          }}
        />
      </div>
    );
  };

  const getDropDownPopup = (): ReactElement<any> => {
    return (
      <ClickableDiv
        className={`flex-align-items-center ${styles.customWidth}`}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
        }}
      >
        <PosterPublishingOptionsPanel
          panelDetails={{
            displayType: DisplayType.DROPDOWN,
            onClose: () => {
              dispatch(updatePublishDropDownState(false));
            },
          }}
          recommendedOptions={recommendedOptions}
          defaultPanelFlowState={defaultPanelFlowState}
          isPosterMaker={window.PMW.isReactPosterMaker() ? 1 : 0}
          idPosterHash={window.posterEditor?.whiteboard?.id !== undefined && Number(window.posterEditor?.whiteboard?.id) > 0 ? window.posterEditor?.whiteboard?.hashedID : ''}
          preselectPaidProduct={window.PMW.isEmbedded() && isUserPremium()}
          isPremium={window.posterEditor?.whiteboard?.getCurrentPage().isPremium() ? 1 : 0}
          posterType={window.posterEditor?.whiteboard?.type.name}
          idGalleryTemplate={window.posterEditor?.whiteboard?.getGalleryTemplateId()}
          idTemplate={window.posterEditor?.whiteboard?.getTemplateId()}
          uc={window.posterEditor?.whiteboard?.savePoster.hasUnsavedChanges() ? 1 : 0}
          isVideo={window.posterEditor?.whiteboard?.isVideo() ? 1 : 0}
          hasVideoItem={window.posterEditor?.whiteboard?.getCurrentPage().items.hasVideoItem() ? 1 : 0}
          isExpensiveVideo={window.posterEditor?.whiteboard?.getCurrentPage().isExpensiveVideo() ? 1 : 0}
          isTransparent={window.posterEditor?.whiteboard?.getCurrentPage().isTransparent() ? 1 : 0}
          hasClickableLink={window.posterEditor?.whiteboard?.getCurrentPage().items.hasClickableItem() ? 1 : 0}
          pw={window.posterEditor?.whiteboard?.width}
          ph={window.posterEditor?.whiteboard?.height}
          ptw={window.posterEditor?.whiteboard?.userWidth}
          pth={window.posterEditor?.whiteboard?.userHeight}
          ptu={window.posterEditor?.whiteboard?.units}
        />
      </ClickableDiv>
    );
  };

  const onOpenPublishOptions = (): void => {
    const poster = window.posterEditor?.whiteboard;
    if (isRepublishButton) {
      return;
    }
    if (poster) {
      const imageSrc = poster.getImageSrcOfUploadingImage();

      if (areImagesUploading && imageSrc) {
        openUploadingImagesModal({
          title: window.i18next.t('pmwjs_uploading_images'),
          text: window.i18next.t('pmwjs_uploading_images_publishing_modal_description'),
          imgSrc: imageSrc,
        });
      } else {
        dispatch(updatePublishDropDownState(true));
      }
    } else {
      dispatch(updatePublishDropDownState(true));
    }
  };

  const getPublishOptionInDropdown = (): ReactElement<any> => {
    return (
      <Icon
        key={`${isPublishDropDownOpen}`}
        icon="icon-menu-dots"
        size={IconSize.SIZE_ICON_20}
        type={IconType.GHOST}
        shape={IconShape.SQUARE}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
          dispatch(updatePublishDropDownState(!isPublishDropDownOpen));
        }}
      />
    );
  };

  return (
    <>
      <Dropdown
        selector={getDropDownSelector()}
        popup={getDropDownPopup()}
        onClose={() => {
          dispatch(updatePublishDropDownState(false));
        }}
        popUpHasCustomWidth
        popUpHasCustomHeight
        popupClassName={`flex-align-items-center ${styles.customWidth}`}
        maxHeight={602}
        popupSpacingClass="spacing-p-0"
        leftOffset={-10}
        doCloseOnInnerClick={false}
        position={DROPDOWN_POSITION.FIXED}
        open={isPublishDropDownOpen}
        onOpen={onOpenPublishOptions}
        isControlled
      />
      {isRepublishButton ? getPublishOptionInDropdown() : null}
    </>
  );
}
